// Оркестратор записи: Web Locks (одна пишущая вкладка), автозапуск при live,
// восстановление после F5, дозаливка чанков прошлых заходов, heartbeat здоровья.

import { HEARTBEAT_INTERVAL_MS } from '@besedka/shared';
import type { CallMediaHealth, HealthReport, RoomState } from '@besedka/shared';
import * as micDevice from '../lib/micDevice.js';
import { Recorder } from './recorder.js';
import { uploader } from './uploader.js';

export interface RecordingUiState {
  active: boolean;
  lockBusy: boolean; // запись идёт в другой вкладке
  segmentNo: number | null;
  level: number;
  sampleRate: number;
  pendingChunks: number;
  uploadLagMs: number;
  lastError: string | null;
}

type Listener = (state: RecordingUiState) => void;

const recorder = new Recorder();

export class RecordingController {
  private muted = false;
  private episodeId: number | null = null;

  /** Общий мьют: тишина в raw-дорожку (recorder) + флаг в телеметрию. */
  setMuted(muted: boolean): void {
    this.muted = muted;
    recorder.muted = muted;
  }
  private participantId: number | null = null;
  private epochTs: number | null = null;
  private lockHeld = false;
  private lockBusy = false;
  private lockRequested = false;
  private startingCapture = false;
  private releaseLock: (() => void) | null = null;
  private wantRecording = false;
  private stopHeartbeat: (() => void) | null = null;
  private listeners = new Set<Listener>();
  private lastError: string | null = null;
  private pageState: NonNullable<HealthReport['pageState']> = document.visibilityState === 'visible'
    ? 'visible'
    : 'hidden';
  private pageStateChangedAt = Date.now();

  getWebrtcState: () => HealthReport['webrtc'] = () => 'off';
  getCallHealth: () => CallMediaHealth | undefined = () => undefined;
  /** Название микрофона вне записи (LiveKit-трек) — задаёт RoomPage. */
  getMicLabel: () => string = () => '';
  sendHealth: (report: HealthReport) => void = () => {};

  constructor() {
    recorder.onStatus = () => this.emit();
    uploader.onStats(() => this.emit());
    // Дозаливаем всё, что осталось с прошлых заходов/крашей
    uploader.kick();
    window.addEventListener('beforeunload', (event) => {
      // Держим страницу и во время записи, и пока не долиты чанки
      if (recorder.active || uploader.stats.pendingChunks > 0) {
        event.preventDefault();
        event.returnValue = '';
      }
    });
    const setPageState = (next: NonNullable<HealthReport['pageState']>) => {
      if (this.pageState !== next) {
        this.pageState = next;
        this.pageStateChangedAt = Date.now();
      }
      this.reportHealthNow();
    };
    document.addEventListener('visibilitychange', () => {
      setPageState(document.visibilityState === 'visible' ? 'visible' : 'hidden');
    });
    // Page Lifecycle API не везде типизирован, но Chrome посылает freeze до
    // полной остановки main thread — успеваем немедленно предупредить остальных.
    document.addEventListener('freeze', () => setPageState('frozen'));
    document.addEventListener('resume', () => {
      setPageState(document.visibilityState === 'visible' ? 'visible' : 'hidden');
    });
    window.addEventListener('pagehide', () => setPageState('hidden'));
    window.addEventListener('pageshow', () => {
      setPageState(document.visibilityState === 'visible' ? 'visible' : 'hidden');
    });
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.uiState());
    return () => this.listeners.delete(listener);
  }

  uiState(): RecordingUiState {
    return {
      active: recorder.status.active,
      lockBusy: this.lockBusy,
      segmentNo: recorder.status.segmentNo,
      level: recorder.status.level,
      sampleRate: recorder.status.sampleRate,
      pendingChunks: uploader.stats.pendingChunks,
      uploadLagMs: uploader.stats.uploadLagMs,
      lastError: this.lastError ?? uploader.stats.lastError
    };
  }

  private emit(): void {
    const state = this.uiState();
    for (const listener of this.listeners) listener(state);
  }

  /** Вызывается на каждый state.update из WS. Сервер — источник правды (спека §3.4). */
  onRoomState(state: RoomState, participantId: number | null): void {
    this.participantId = participantId;
    const episode = state.episode;
    const shouldRecord =
      episode.status === 'live' &&
      participantId !== null &&
      episode.epochTs !== null &&
      Number(episode.id) === Number(state.activeEpisodeId ?? episode.id);

    if (shouldRecord) {
      this.episodeId = Number(episode.id);
      this.epochTs = Number(episode.epochTs);
      this.wantRecording = true;
      void this.acquireAndStart();
      this.startHeartbeat();
    } else {
      this.wantRecording = false;
      if (recorder.active) void this.stopCapture();
      // heartbeat продолжаем и вне записи — панель здоровья живёт всегда
      if (participantId !== null) this.startHeartbeat();
    }
  }

  private async acquireAndStart(): Promise<void> {
    if (recorder.active || this.lockHeld === true) {
      void this.startCaptureIfNeeded();
      return;
    }
    if (!('locks' in navigator)) {
      // Нет Web Locks (старый браузер) — пишем без защиты от второй вкладки
      this.lockHeld = true;
      void this.startCaptureIfNeeded();
      return;
    }
    // Один lock-запрос за раз: без этого каждый state.update ставил бы в
    // очередь Web Locks ещё один ожидающий запрос
    if (this.lockRequested) return;
    this.lockRequested = true;
    const name = `besedka-rec-${this.episodeId}`;
    void navigator.locks
      .request(name, { ifAvailable: true }, async (lock) => {
        if (!lock) {
          // Запись идёт в другой вкладке; ждём в очереди — перехватим при её смерти
          this.lockBusy = true;
          this.emit();
          await navigator.locks.request(name, async () => {
            this.lockBusy = false;
            this.lockHeld = true;
            this.emit();
            await this.holdWhileRecording();
          });
          return;
        }
        this.lockHeld = true;
        this.emit();
        await this.holdWhileRecording();
      })
      .finally(() => {
        this.lockRequested = false;
      });
  }

  private async holdWhileRecording(): Promise<void> {
    await this.startCaptureIfNeeded();
    // Держим lock, пока запись нужна
    await new Promise<void>((resolve) => {
      this.releaseLock = resolve;
    });
    this.lockHeld = false;
    this.releaseLock = null;
  }

  private async startCaptureIfNeeded(): Promise<void> {
    if (this.startingCapture) return; // старт уже идёт — вторая попытка запрещена
    if (!this.wantRecording || recorder.active || !this.lockHeld) return;
    if (this.episodeId === null || this.participantId === null || this.epochTs === null) return;
    this.startingCapture = true;
    try {
      this.lastError = null;
      await recorder.start(this.episodeId, this.participantId, this.epochTs);
      // Пишем live этим устройством — запоминаем его для будущих сессий
      if (recorder.active) micDevice.rememberLive(recorder.micDeviceId, recorder.micLabel);
    } catch (error) {
      this.lastError = `Захват не стартовал: ${(error as Error).message}`;
      this.emit();
      // Повтор через 5 с (микрофон могли не дать сразу)
      setTimeout(() => void this.startCaptureIfNeeded(), 5000);
    } finally {
      this.startingCapture = false;
    }
  }

  private async stopCapture(): Promise<void> {
    await recorder.stop(this.episodeId ?? undefined, this.participantId ?? undefined);
    uploader.kick();
    this.releaseLock?.();
  }

  private startHeartbeat(): void {
    if (this.stopHeartbeat) return;
    const tick = () => this.reportHealthNow();
    // Таймер в Web Worker: Chrome троттлит setInterval фоновых вкладок до 1/мин,
    // из-за чего телеметрия «протухала» — воркер от видимости вкладки не зависит
    try {
      const blob = new Blob([`setInterval(() => postMessage(1), ${HEARTBEAT_INTERVAL_MS});`], {
        type: 'text/javascript'
      });
      const url = URL.createObjectURL(blob);
      const worker = new Worker(url);
      worker.onmessage = tick;
      this.stopHeartbeat = () => {
        worker.terminate();
        URL.revokeObjectURL(url);
        this.stopHeartbeat = null;
      };
    } catch {
      const timer = window.setInterval(tick, HEARTBEAT_INTERVAL_MS);
      this.stopHeartbeat = () => {
        window.clearInterval(timer);
        this.stopHeartbeat = null;
      };
    }
  }

  /** Отправляется и по таймеру, и немедленно при lock/hide/freeze. */
  reportHealthNow(): void {
    if (this.participantId === null) return;
    this.watchdog();
    this.sendHealth({
      recording: recorder.status.active,
      level: recorder.status.level,
      rawFrameAgeMs: recorder.status.active
        ? Math.max(0, Date.now() - recorder.lastFrameAt)
        : null,
      sampleRate: recorder.status.sampleRate,
      micSampleRate: recorder.micSampleRate,
      micLabel: recorder.micLabel || this.getMicLabel(),
      segmentNo: recorder.status.segmentNo,
      pendingChunks: uploader.stats.pendingChunks,
      uploadLagMs: uploader.stats.uploadLagMs,
      webrtc: this.getWebrtcState(),
      pageState: this.pageState,
      pageStateAgeMs: Math.max(0, Date.now() - this.pageStateChangedAt),
      call: this.getCallHealth(),
      speaking: recorder.status.level > 0.02 && !this.muted,
      muted: this.muted
    });
  }

  /**
   * Захват «заморожен» (телефон заблокировали, ОС усыпила аудио) — сэмплы не
   * идут, хотя recorder формально активен. Перезапускаем капчер: закрывается
   * старый сегмент (хвост уже в IndexedDB) и открывается новый; дыра честно
   * попадёт в метаданные выпуска.
   */
  private restartingCapture = false;
  private watchdog(): void {
    if (!recorder.active || this.restartingCapture) return;
    // Три пропущенных heartbeat достаточно, чтобы считать raw-граф
    // замороженным. Старые 15 секунд делали индикатор слишком оптимистичным.
    if (Date.now() - recorder.lastFrameAt < HEARTBEAT_INTERVAL_MS * 3) return;
    this.restartingCapture = true;
    this.lastError = 'Захват прерывался (телефон был заблокирован?) — сегмент перезапущен';
    void recorder
      .stop(this.episodeId ?? undefined, this.participantId ?? undefined)
      .then(() => {
        uploader.kick();
        this.restartingCapture = false;
        return this.startCaptureIfNeeded();
      })
      .catch(() => {
        this.restartingCapture = false;
      });
  }

  /**
   * Смена микрофона посреди записи: тот же поток, что и watchdog — закрыть
   * сегмент (хвост в IndexedDB), открыть новый с новым устройством/частотой.
   * Даёт ~1-2 с честной тишины в raw-дорожке на стыке. Вне записи — no-op:
   * следующий start() сам возьмёт актуальный выбор из micDevice.
   */
  async switchMic(): Promise<void> {
    if (!recorder.active || this.restartingCapture) return;
    this.restartingCapture = true;
    try {
      await recorder.stop(this.episodeId ?? undefined, this.participantId ?? undefined);
      uploader.kick();
    } finally {
      this.restartingCapture = false;
    }
    await this.startCaptureIfNeeded();
  }

  dispose(): void {
    this.stopHeartbeat?.();
    this.wantRecording = false;
    if (recorder.active) void this.stopCapture();
  }
}

export const recordingController = new RecordingController();
